import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Icon, type IconName } from "@wato/ui";
import { useApi } from "../../api/context";
import { mayUse } from "../roles/role-label";
import { useModuleFlags } from "../workspace/useModules";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { loadHomeSummary } from "./home-summary";

const settleCount = (run: () => Promise<number>): Promise<number | null> =>
  Promise.resolve()
    .then(run)
    .then(
      (n) => n,
      () => null,
    );

interface CardDef {
  readonly id: "chats" | "orders" | "bookings";
  readonly to: string;
  readonly icon: IconName;
  readonly tile: string;
  readonly labelKey:
    | "dashboard.home.today.waiting"
    | "dashboard.home.today.newOrders"
    | "dashboard.home.today.bookingsToday";
  readonly load: () => Promise<number>;
}

/** OWN-219 B: the three big «needs your attention» cards; a card hides when its module/permission is off. */
export function HomeAttention({ workspaceId }: { readonly workspaceId: string }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const modules = useModuleFlags();
  const num = useMemo(
    () => new Intl.NumberFormat(locale === "ar" ? "ar-u-nu-latn" : "en"),
    [locale],
  );
  const cards = useMemo<CardDef[]>(() => {
    const list: CardDef[] = [];
    if (mayUse(workspace, "inbox.view")) {
      list.push({
        id: "chats",
        to: "conversations",
        icon: "inbox",
        tile: "bg-app-accent text-app-on-accent",
        labelKey: "dashboard.home.today.waiting",
        load: () => loadHomeSummary(api, workspaceId).then((s) => s.unread),
      });
    }
    if (mayUse(workspace, "commerce.view") && modules.isOn("store")) {
      list.push({
        id: "orders",
        to: "store/orders",
        icon: "cart",
        tile: "bg-warning-400 text-app-on-logo",
        labelKey: "dashboard.home.today.newOrders",
        load: () => api.listWaOrders(workspaceId, { status: "new" }).then((o) => o.length),
      });
    }
    if (mayUse(workspace, "commerce.view") && modules.isOn("bookings")) {
      list.push({
        id: "bookings",
        to: "bookings",
        icon: "calendar",
        tile: "bg-info-400 text-app-on-logo",
        labelKey: "dashboard.home.today.bookingsToday",
        load: () => {
          const from = new Date();
          from.setHours(0, 0, 0, 0);
          const to = new Date(from);
          to.setDate(to.getDate() + 1);
          return api
            .listBookings(workspaceId, { from: from.toISOString(), to: to.toISOString() })
            .then((b) => b.length);
        },
      });
    }
    return list;
  }, [api, workspace, workspaceId]);
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  useEffect(() => {
    let live = true;
    setCounts({});
    for (const c of cards) {
      void settleCount(c.load).then((n) => live && setCounts((p) => ({ ...p, [c.id]: n })));
    }
    return () => {
      live = false;
    };
  }, [cards]);
  if (cards.length === 0) return null;
  return (
    <section
      aria-label={t("dashboard.home.today.attention")}
      data-testid="home-attention"
      className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(15rem,1fr))]"
    >
      {cards.map((c) => {
        const n = counts[c.id];
        return (
          <Link
            key={c.id}
            to={`/w/${workspaceId}/${c.to}`}
            data-testid={`home-attention-${c.id}`}
            className="flex items-center gap-4 rounded-2xl border border-app-line bg-app-surface p-6 text-app-fg no-underline hover:bg-app-surface-2"
          >
            <span
              className={`flex size-14 shrink-0 items-center justify-center rounded-2xl ${c.tile}`}
            >
              <Icon name={c.icon} className="size-6" />
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-4xl font-bold leading-none tabular-nums">
                {typeof n === "number" ? num.format(n) : "—"}
              </span>
              <span className="text-sm text-app-muted">{t(c.labelKey)}</span>
            </span>
          </Link>
        );
      })}
    </section>
  );
}

type ChipLabel =
  | "dashboard.home.today.createCampaign"
  | "dashboard.home.today.createProduct"
  | "dashboard.home.today.createReply"
  | "dashboard.home.today.createContact";

/** OWN-219 B: quick-create chips (campaign, product, auto-reply, contact), gated by permission. */
export function HomeCreateChips({ workspaceId }: { readonly workspaceId: string }) {
  const { t } = useI18n();
  const { workspace } = useWorkspace();
  const modules = useModuleFlags();
  const chips: { id: string; to: string; icon: IconName; label: ChipLabel; ok: boolean }[] = [
    {
      id: "campaign",
      to: "broadcasts/new",
      icon: "megaphone",
      label: "dashboard.home.today.createCampaign",
      ok: mayUse(workspace, "broadcasts.create"),
    },
    {
      id: "product",
      to: "store",
      icon: "cart",
      label: "dashboard.home.today.createProduct",
      ok: mayUse(workspace, "commerce.edit") && modules.isOn("store"),
    },
    {
      id: "reply",
      to: "flows",
      icon: "bolt",
      label: "dashboard.home.today.createReply",
      ok: mayUse(workspace, "flows.create"),
    },
    {
      id: "contact",
      to: "contacts",
      icon: "users",
      label: "dashboard.home.today.createContact",
      ok: mayUse(workspace, "contacts.create"),
    },
  ];
  const shown = chips.filter((c) => c.ok);
  if (shown.length === 0) return null;
  return (
    <nav
      aria-label={t("dashboard.home.today.create")}
      data-testid="home-create-chips"
      className="flex flex-wrap gap-3"
    >
      {shown.map((c) => (
        <Link
          key={c.id}
          to={`/w/${workspaceId}/${c.to}`}
          data-testid={`home-create-${c.id}`}
          className="inline-flex min-h-12 items-center gap-2.5 rounded-xl border border-dashed border-app-line px-4 text-sm font-medium text-app-fg no-underline hover:bg-app-surface-2"
        >
          <Icon name={c.icon} className="size-4 text-app-accent" />
          {t(c.label)}
        </Link>
      ))}
    </nav>
  );
}
